// Contrat commun de la chaîne de la dépense (lot L4, section 4 de docs/cadrage/lot-L4.md ; D-013,
// D-141 à D-143). Posé par la partie B (finances) avant les autres : la partie A (patrimoine) et la
// partie C (achats, travaux) codent contre ce fichier. Chaîne : besoin exprimé par un service,
// validation hiérarchique, engagement par le financier avec contrôle du crédit disponible (prévu +
// mouvements - engagé, refus sinon, dérogation motivée du maire ou président), service fait, facture
// (table metier.facture, D-142), liquidation (situation de travaux possible, projet facultatif),
// mandat ordonnancé par le maire, le président ou un délégué daté (jamais par le compte qui a engagé,
// refus en base, D-143), paiement confirmé saisi avec la référence du comptable public (doublon
// refusé). Le logiciel ne paie jamais. Les cinq montants d'une ligne (prévu, engagé, liquidé,
// ordonnancé, payé) sont calculés en base par la vue metier.execution_ligne, jamais saisis.
import { z } from 'zod';

const uuid = z.string().uuid('Identifiant invalide');
const jour = z.iso.date('Date AAAA-MM-JJ attendue');
const texte = (max: number) => z.string().trim().min(1).max(max);
const observation = z.string().trim().max(2000).optional();
const pieces = z.array(uuid).max(10).optional();

/** Montant en francs CFA : entier strictement positif. */
export const schemaMontantDepense = z.number().int('Montant entier en francs CFA').positive('Montant strictement positif').max(1_000_000_000_000);
export const schemaMotifDepense = z.string().trim().min(3, 'Motif de 3 caractères au moins').max(1000);

// Codes d'erreur de la chaîne -------------------------------------------------------------------

export const ERREURS_DEPENSE = {
  credit_insuffisant: 'Crédit disponible insuffisant sur la ligne budgétaire',
  separation_des_fonctions: 'Le compte qui a engagé la dépense ne peut pas ordonnancer le mandat',
  reference_paiement_doublon: 'Cette référence de paiement du comptable public est déjà enregistrée dans la collectivité',
} as const;
export type CodeErreurDepense = keyof typeof ERREURS_DEPENSE;
export const CODES_ERREUR_DEPENSE = Object.keys(ERREURS_DEPENSE) as [CodeErreurDepense, ...CodeErreurDepense[]];

/** Détails joints à l'erreur credit_insuffisant (montants en francs CFA). */
export interface DetailsCreditInsuffisant {
  ligne_id: string;
  disponible: number;
  demande: number;
  manque: number;
  /** Engagement conservé à l'état refuse (trace du refus). */
  engagement_id: string | null;
}

// Étapes et états ------------------------------------------------------------------------------

export const ETAPES_DEPENSE = {
  besoin: 'Besoin exprimé',
  validation: 'Besoin validé',
  engagement: 'Engagé',
  service_fait: 'Service fait',
  liquidation: 'Liquidé',
  ordonnancement: 'Ordonnancé',
  paiement: 'Payé',
} as const;
export type EtapeDepense = keyof typeof ETAPES_DEPENSE;

export const ETATS_BESOIN = { a_valider: 'À valider', valide: 'Validé', refuse: 'Refusé', engage: 'Engagé', annule: 'Annulé' } as const;
export type EtatBesoin = keyof typeof ETATS_BESOIN;
export const CODES_ETATS_BESOIN = Object.keys(ETATS_BESOIN) as [EtatBesoin, ...EtatBesoin[]];

/** refuse : tentative refusée pour crédit insuffisant, conservée sans numéro et hors du montant engagé. */
export const ETATS_ENGAGEMENT = { engage: 'Engagé', refuse: 'Refusé (crédit insuffisant)', annule: 'Annulé' } as const;
export type EtatEngagement = keyof typeof ETATS_ENGAGEMENT;
export const CODES_ETATS_ENGAGEMENT = Object.keys(ETATS_ENGAGEMENT) as [EtatEngagement, ...EtatEngagement[]];

export const ETATS_FACTURE = { recue: 'Reçue', liquidee: 'Liquidée', annulee: 'Annulée' } as const;
export type EtatFacture = keyof typeof ETATS_FACTURE;

export const ETATS_LIQUIDATION = { liquidee: 'Liquidée', annulee: 'Annulée' } as const;
export type EtatLiquidation = keyof typeof ETATS_LIQUIDATION;

export const ETATS_MANDAT = { emis: 'Ordonnancé', paye: 'Payé', annule: 'Annulé' } as const;
export type EtatMandat = keyof typeof ETATS_MANDAT;

export const ETATS_PAIEMENT_DEPENSE = { confirme: 'Confirmé', annule: 'Annulé' } as const;
export type EtatPaiementDepense = keyof typeof ETATS_PAIEMENT_DEPENSE;

export const MODES_PAIEMENT_DEPENSE = { virement: 'Virement', cheque: 'Chèque', especes: 'Espèces', mobile_money: 'Mobile money', autre: 'Autre' } as const;
export type ModePaiementDepense = keyof typeof MODES_PAIEMENT_DEPENSE;
export const CODES_MODES_PAIEMENT_DEPENSE = Object.keys(MODES_PAIEMENT_DEPENSE) as [ModePaiementDepense, ...ModePaiementDepense[]];

/** Types de numéros attribués par la numérotation existante (D-149). */
export const TYPES_NUMEROS_DEPENSE = { engagement: 'ENG', facture: 'FAC', liquidation: 'LIQ', mandat: 'MAN' } as const;

// Cinq montants d'une ligne -----------------------------------------------------------------------

export const schemaCinqMontants = z.object({
  prevu: z.number().int(),
  engage: z.number().int(),
  liquide: z.number().int(),
  ordonnance: z.number().int(),
  paye: z.number().int(),
});
export type CinqMontants = z.infer<typeof schemaCinqMontants>;

/** Montants d'exécution d'une ligne : les cinq montants, les mouvements de crédits et le disponible. */
export interface MontantsLigne extends CinqMontants {
  mouvements: number;
  credits_ouverts: number;
  disponible: number;
}

/** Crédit disponible d'une ligne de dépense : prévu + mouvements - engagé (D-141). */
export function creditDisponible(m: { prevu: number; mouvements: number; engage: number }): number {
  return m.prevu + m.mouvements - m.engage;
}

/** Contrôle d'un engagement : suffisant si le montant demandé ne dépasse pas le disponible. */
export function controlerCredit(disponible: number, demande: number): { suffisant: boolean; manque: number } {
  const manque = Math.max(0, demande - disponible);
  return { suffisant: manque === 0, manque };
}

/** Montants d'exécution complets à partir des montants de base (même calcul que la vue en base). */
export function montantsLigne(b: { prevu: number; mouvements: number; engage: number; liquide: number; ordonnance: number; paye: number }): MontantsLigne {
  const creditsOuverts = b.prevu + b.mouvements;
  return { ...b, credits_ouverts: creditsOuverts, disponible: creditDisponible(b) };
}

// Écritures ------------------------------------------------------------------------------------

export const schemaCreationBesoin = z.strictObject({
  service_id: uuid,
  objet: texte(300),
  description: z.string().trim().max(4000).optional(),
  estimation: schemaMontantDepense,
  /** Ligne budgétaire suggérée par le service ; le financier choisit la ligne à l'engagement. */
  ligne_id: uuid.optional(),
});
export type CreationBesoin = z.infer<typeof schemaCreationBesoin>;

export const schemaValidationBesoin = z.strictObject({ observation });
export const schemaMotifActe = z.strictObject({ motif: schemaMotifDepense });
export type MotifActe = z.infer<typeof schemaMotifActe>;

export const schemaCreationEngagement = z.strictObject({
  besoin_id: uuid,
  ligne_id: uuid,
  /** Tiers bénéficiaire (fournisseur ou prestataire), saisi librement ; fournisseur_id relève des achats. */
  tiers: texte(200),
  fournisseur_id: uuid.optional(),
  montant: schemaMontantDepense,
  objet: z.string().trim().max(300).optional(),
});
export type CreationEngagement = z.infer<typeof schemaCreationEngagement>;

/** Dérogation au contrôle des crédits accordée par le maire ou président sur un engagement refusé. */
export const schemaDerogation = z.strictObject({ motif: schemaMotifDepense });

export const schemaServiceFait = z.strictObject({ date: jour, observation, document_ids: pieces });
export type SaisieServiceFait = z.infer<typeof schemaServiceFait>;

export const schemaCreationFacture = z.strictObject({
  fournisseur_nom: texte(200),
  /** Fournisseur du module achats (partie C), facultatif. */
  fournisseur_id: uuid.optional(),
  numero_fournisseur: texte(60),
  date_facture: jour,
  montant: schemaMontantDepense,
  /** Rattachements posés par les achats (partie C, D-142), facultatifs. */
  commande_id: uuid.optional(),
  livraison_id: uuid.optional(),
  document_ids: pieces,
});
export type CreationFacture = z.infer<typeof schemaCreationFacture>;

export const schemaCreationLiquidation = z
  .strictObject({
    engagement_id: uuid,
    facture_id: uuid.optional(),
    /** Facture saisie avec la liquidation (sinon facture_id). */
    facture: schemaCreationFacture.optional(),
    montant: schemaMontantDepense,
    /** Situation de travaux (partie C, D-146) ; projet_id rattache la liquidation à un projet. */
    situation: z.boolean().default(false),
    projet_id: uuid.optional(),
    observation,
  })
  .refine((v) => Boolean(v.facture_id) !== Boolean(v.facture), { message: 'Une facture est exigée : facture_id ou facture, pas les deux', path: ['facture_id'] })
  .refine((v) => !v.situation || Boolean(v.projet_id), { message: 'Une situation de travaux est rattachée à un projet', path: ['projet_id'] });
export type CreationLiquidation = z.input<typeof schemaCreationLiquidation>;

export const schemaCreationMandat = z.strictObject({ liquidation_id: uuid, observation });
export type CreationMandat = z.infer<typeof schemaCreationMandat>;

export const schemaPaiementConfirme = z.strictObject({
  mandat_id: uuid,
  date_paiement: jour,
  mode: z.enum(CODES_MODES_PAIEMENT_DEPENSE),
  /** Référence du comptable public (bordereau, chèque, virement) : unique dans la collectivité. */
  reference: z.string().trim().min(3, 'Référence de 3 caractères au moins').max(80),
  observation,
});
export type PaiementConfirme = z.infer<typeof schemaPaiementConfirme>;

// Filtres --------------------------------------------------------------------------------------

const limite = z.coerce.number().int().min(1).max(500).default(200);
export const schemaFiltreBesoins = z.strictObject({ etat: z.enum(CODES_ETATS_BESOIN).optional(), service_id: uuid.optional(), limite });
export const schemaFiltreEngagements = z.strictObject({ etat: z.enum(CODES_ETATS_ENGAGEMENT).optional(), ligne_id: uuid.optional(), budget_id: uuid.optional(), limite });
export const schemaFiltreChaine = z.strictObject({ ligne_id: uuid.optional(), budget_id: uuid.optional(), etat: z.enum(['liquidee', 'annulee', 'emis', 'paye', 'annule']).optional(), a_ordonnancer: z.enum(['oui', 'non']).optional(), projet_id: uuid.optional(), limite });

// Réponses -------------------------------------------------------------------------------------

export interface PersonneActe {
  compte_id: string;
  nom: string | null;
  le: string;
}

export interface BesoinDepense {
  id: string;
  service: { id: string; nom: string | null };
  objet: string;
  description: string | null;
  estimation: number;
  ligne_suggeree: { id: string; code: string; libelle: string } | null;
  etat: EtatBesoin;
  demande: PersonneActe;
  validation: (PersonneActe & { observation: string | null }) | null;
  motif_refus: string | null;
  engagement_id: string | null;
}

export interface EngagementDepense {
  id: string;
  numero: string | null;
  besoin: { id: string; objet: string; service_nom: string | null };
  ligne: { id: string; code: string; libelle: string; section: string; exercice: number };
  tiers: string;
  fournisseur_id: string | null;
  montant: number;
  objet: string | null;
  etat: EtatEngagement;
  /** Disponible de la ligne constaté au moment de l'engagement ou du refus. */
  disponible_constate: number;
  derogation: (PersonneActe & { motif: string }) | null;
  engage: PersonneActe;
  services_faits: { id: string; date: string; observation: string | null; par: PersonneActe }[];
  liquide: number;
  ordonnance: number;
  paye: number;
  etape: EtapeDepense;
  motif_annulation: string | null;
}

export interface FactureDepense {
  id: string;
  numero: string;
  fournisseur_nom: string;
  fournisseur_id: string | null;
  numero_fournisseur: string;
  date_facture: string;
  montant: number;
  commande_id: string | null;
  livraison_id: string | null;
  etat: EtatFacture;
  pieces: { id: string; titre: string }[];
}

export interface LiquidationDepense {
  id: string;
  numero: string;
  engagement: { id: string; numero: string | null; tiers: string; montant: number };
  ligne: { id: string; code: string; libelle: string };
  facture: FactureDepense;
  montant: number;
  situation: boolean;
  projet_id: string | null;
  etat: EtatLiquidation;
  liquide: PersonneActe;
  mandat: { id: string; numero: string; etat: EtatMandat } | null;
  motif_annulation: string | null;
}

export interface MandatDepense {
  id: string;
  numero: string;
  liquidation: { id: string; numero: string; montant: number };
  engagement: { id: string; numero: string | null; tiers: string; engage_par: string };
  ligne: { id: string; code: string; libelle: string; section: string };
  montant: number;
  etat: EtatMandat;
  ordonnateur: PersonneActe & { delegation_id: string | null };
  paiement: PaiementDepense | null;
  motif_annulation: string | null;
}

export interface PaiementDepense {
  id: string;
  mandat_id: string;
  date_paiement: string;
  mode: ModePaiementDepense;
  reference: string;
  montant: number;
  etat: EtatPaiementDepense;
  saisi: PersonneActe;
}

/** Étape atteinte par un engagement (affichage de la chaîne). */
export function etapeEngagement(e: { etat: EtatEngagement; services_faits: number; liquide: number; ordonnance: number; paye: number; montant: number }): EtapeDepense {
  if (e.etat !== 'engage') return 'validation';
  if (e.paye > 0) return 'paiement';
  if (e.ordonnance > 0) return 'ordonnancement';
  if (e.liquide > 0) return 'liquidation';
  if (e.services_faits > 0) return 'service_fait';
  return 'engagement';
}
